import api from '../../services/api';

/**
 * The signed-in user's own profile.
 *
 * Takes no id: the backend reads `request.user`. What comes back depends on the
 * profile — `UserDetailSerializer.to_representation` flattens the matching
 * `*ProfileSerializer` into the response, so an admin carries `rut` and
 * `permission`, a seller adds `company_name`, an operator `warehouse_name`, a
 * driver `phone`. Read what arrives, do not assume a shape.
 */
export const getCurrentUserRequest = () => api.get('get_user/');

/**
 * The three writes below are keyed by user id and the view rejects anything but
 * your own (`request.user != current_user` → 403).
 *
 * **Pass the id from the session, never the one in `get_user/`.**
 * `DriverProfileSerializer` declares `id` among its fields, and the flattening
 * above overwrites the user's id with the Driver's — so for that profile the
 * id in the payload is the wrong one and every call here would 403.
 */
export const updateUserNamesRequest = (userId, data) =>
  api.patch(`update_user_names/${userId}/`, data);

export const updateEmailRequest = (userId, email) =>
  api.patch(`update_email/${userId}/`, { email });

/** Replaces the whole array, so removing one is a write of the rest. Max 5. */
export const updateOperativeMailsRequest = (userId, operativeMails) =>
  api.patch(`update_operative_mails/${userId}/`, { operative_mails: operativeMails });

/**
 * Step one of changing the password: mails a six-digit code to the account.
 *
 * The code lives 5 minutes in Redis. Asking again replaces it, so the last one
 * sent is the only one that works.
 */
export const requestSecureCodeRequest = () => api.get('get_secure_code/');

/** Step two: trades the code for a token, also good for 5 minutes. */
export const verifySecureCodeRequest = (code) => api.post('verify_secure_code/', { code });

/**
 * Step three. The backend runs Django's `validate_password`, so a rejection
 * here is a real message worth showing rather than a generic failure.
 */
export const changePasswordRequest = (token, password) =>
  api.post('change_password/', {
    token,
    new_password1: password,
    new_password2: password,
  });
